import { MessageBus } from '@setplex/mqbw'

/**
 * Represents an immutable point-in-time snapshot of a Map.
 * Each snapshot captures the complete state of the Map at the moment of creation.
 *
 * @typeParam K - The type of keys in the Map
 * @typeParam V - The type of values in the Map
 */
export class Snapshot<K, V> {
  /** Unix timestamp (milliseconds) when this snapshot was created */
  readonly ts: number

  /** Deep clone of the Map at snapshot time */
  readonly map: Map<K, V>

  /**
   * Creates a new snapshot of the provided Map.
   * @param map - The Map to snapshot (will be deep cloned)
   */
  constructor(map: Map<K, V>) {
    this.ts = Date.now()
    try {
      this.map = structuredClone(map)
    } catch (ignore) {
      // TODO: find, what is causing structuredClone to fail in some cases and fix it
      this.map = new Map(map) // Fallback to shallow clone if structuredClone fails
    }
  }
}

/**
 * A fixed-capacity circular buffer (ring buffer) implementation.
 * When full, new items overwrite the oldest items automatically.
 *
 * @typeParam V - The type of elements stored in the buffer
 *
 * @example
 * const buffer = new RingBuffer<number>(3);
 * buffer.push(1); buffer.push(2); buffer.push(3);
 * buffer.push(4); // Overwrites 1
 * buffer.at(0); // Returns 2
 */
export class RingBuffer<V> {
  /** Internal storage array */
  private buf: V[]

  /** Maximum capacity of the buffer */
  private cap: number

  /** Index of the oldest element in the buffer */
  private start: number

  /** Current number of elements (0 to capacity) */
  private size: number

  /**
   * Creates a new ring buffer with the specified capacity.
   * @param capacity - Maximum number of elements the buffer can hold
   */
  constructor(capacity: number) {
    this.buf = new Array<V>(capacity)
    this.cap = capacity
    this.start = 0
    this.size = 0
  }

  /**
   * Adds a new element to the buffer.
   * If at capacity, overwrites the oldest element.
   *
   * @param value - The element to add
   */
  push(value: V): void {
    if (this.cap <= 0) {
      return // No capacity, ignore pushes
    }

    const pos = (this.start + this.size) % this.cap

    this.buf[pos] = value

    if (this.size < this.cap) {
      this.size++
    } else {
      this.start = (this.start + 1) % this.cap
    }
  }

  /**
   * Retrieves an element at the specified logical index.
   * Supports negative indexing (e.g., -1 for last element).
   *
   * @param index - Logical index (0 to length-1, or negative for reverse)
   * @returns The element at the index, or undefined if out of bounds
   *
   * @example
   * buffer.at(0);  // First element
   * buffer.at(-1); // Last element
   */
  at(index: number): V | undefined {
    if (this.cap <= 0) {
      return undefined // No capacity, always empty
    }

    if (index < 0) {
      index = this.size + index
    }

    if (index < 0 || index >= this.size) {
      return undefined
    }

    const real = (this.start + index) % this.cap
    return this.buf[real]
  }

  /**
   * Gets the current number of elements in the buffer.
   * @returns Number of elements (0 to capacity)
   */
  get length(): number {
    return this.size
  }

  /**
   * Gets the maximum capacity of the buffer.
   * @returns Maximum number of elements the buffer can hold
   */
  get capacity(): number {
    return this.cap
  }

  /**
   * Removes all elements from the buffer.
   * Resets to initial empty state without reallocating memory.
   */
  clear(): void {
    this.start = 0
    this.size = 0
  }

  /**
   * Makes the buffer iterable with for...of loops.
   * Iterates elements in logical order (oldest to newest).
   *
   * @yields Elements in the buffer from index 0 to length-1
   */
  *[Symbol.iterator]() {
    for (let i = 0; i < this.size; i++) {
      yield this.at(i)
    }
  }
}

/**
 * A Map that maintains a fixed-capacity history of snapshots with type-safe key-value pairs.
 *
 * Snapshots capture the Map's state at the moment snapshot() is called.
 * Call snapshot() BEFORE making changes to preserve the prior state.
 * The `at()` method returns the first snapshot created at or after the
 * given timestamp, or the current live state if no such snapshot exists.
 *
 * Uses a ring buffer to limit memory usage - oldest snapshots are automatically
 * discarded when the capacity is exceeded.
 *
 * @typeParam T - A record type mapping keys to their specific value types
 *
 * @example
 * type Config = {
 *   count: number;
 *   status: 'active' | 'inactive';
 *   name?: string;
 * };
 *
 * const map = new SnapshotMap<Config>(100);
 * map.set('count', 1);      // ✅ Type-safe: number required
 * map.set('status', 'active'); // ✅ Type-safe: literal union
 * map.set('count', 'wrong'); // ❌ Error: string not assignable to number
 *
 * const t1 = Date.now();
 * map.snapshot(); // Captures count=1, status='active'
 * map.set('count', 2);
 *
 * const state1 = map.at(t1);
 * console.log(state1.map.get('count')); // 1 (snapshot taken at t1)
 */
export class SnapshotMap<T extends Record<string, unknown>> extends Map<
  keyof T,
  T[keyof T]
> {
  /** Ring buffer storing historical snapshots */
  private snapshots: RingBuffer<Snapshot<keyof T, T[keyof T]>>

  /** Temporary map holding pending changes */
  private draft: Map<keyof T, T[keyof T] | undefined> | null = null

  /** Message bus for update notifications */
  updates = new MessageBus()

  /**
   * Creates a new SnapshotMap with specified snapshot capacity.
   * @param capacity - Maximum number of snapshots to retain
   */
  constructor(capacity: number)
  /**
   * Creates a new SnapshotMap with specified snapshot capacity and initial entries.
   * @param capacity - Maximum number of snapshots to retain
   * @param iterable - Initial entries to populate the map
   */
  constructor(
    capacity: number,
    iterable?: Iterable<readonly [keyof T, T[keyof T]]> | null
  )
  constructor(
    capacity: number,
    iterable?: Iterable<readonly [keyof T, T[keyof T]]> | null
  ) {
    super(iterable)
    this.snapshots = new RingBuffer(capacity)
  }

  /**
   * Creates a snapshot-like object representing the current live state.
   * Note: The returned map reference is NOT cloned and points to the live Map.
   * This is intentional for performance - the current state is mutable.
   *
   * @param ts - Timestamp to assign to the current state (defaults to now)
   * @returns A snapshot object with the current Map state
   */
  current(ts = Date.now()): Snapshot<keyof T, T[keyof T]> {
    return {
      ts,
      map: this, // Intentionally not cloned - this is the live state
    }
  }

  /**
   * Creates and stores a snapshot of the current Map state.
   *
   * Best practice: Call this BEFORE making changes to preserve the current state.
   * The snapshot is immutable and captures the state at this exact moment.
   * If at capacity, the oldest snapshot will be discarded.
   *
   * @returns The SnapshotMap instance (for method chaining)
   *
   * @example
   * // Snapshot-before-change pattern with method chaining
   * map.set('status', 'initial')
   *    .snapshot()  // Preserve 'initial'
   *    .set('status', 'processing')
   *    .snapshot()  // Preserve 'processing'
   *    .set('status', 'complete');
   * // Current state is 'complete' (not yet snapshotted)
   *
   * @example
   * // Or without chaining:
   * map.snapshot(); // Save current state
   * map.set('value', 'new'); // Modify after snapshotting
   */
  snapshot(): this {
    const snapshot = new Snapshot(this as Map<keyof T, T[keyof T]>)
    this.snapshots.push(snapshot)
    return this
  }

  /**
   * Retrieves the Map state at or after the given timestamp.
   *
   * Returns the first snapshot with timestamp >= the requested time,
   * or the current live state if all snapshots are older than the timestamp.
   * If no snapshots exist, returns the current state.
   *
   * Use case: "What was the state at time T?" → returns the snapshot
   * taken at or just after T, which captures the state that existed at T.
   *
   * Algorithm:
   * 1. If no snapshots exist → return current state
   * 2. If all snapshots are older than ts → return current state
   * 3. Check last 2 snapshots (fast path for recent queries)
   * 4. Binary search for first snapshot >= ts
   * 5. Return found snapshot or current state
   *
   * Time complexity: O(1) for current state or last 2 snapshots, O(log n) otherwise
   *
   * @param ts - Target timestamp in milliseconds (defaults to current time)
   * @returns Snapshot at or after the timestamp, or current state
   *
   * @example
   * const t0 = Date.now();
   * map.set('value', 'A');
   *
   * const t1 = Date.now();
   * map.snapshot(); // Captures 'A' at t1
   * map.set('value', 'B');
   *
   * const t2 = Date.now();
   * map.snapshot(); // Captures 'B' at t2
   * map.set('value', 'C');
   *
   * // Query different time points:
   * map.at(t0);           // Returns snapshot at t1 (first snapshot >= t0) → 'A'
   * map.at(t1);           // Returns snapshot at t1 → 'A'
   * map.at(t1 + 500);     // Returns snapshot at t2 (first snapshot >= t1+500) → 'B'
   * map.at(t2);           // Returns snapshot at t2 → 'B'
   * map.at(Date.now());   // Returns current state (no snapshot after t2) → 'C'
   */
  at(ts = Date.now()): Snapshot<keyof T, T[keyof T]> {
    const n = this.snapshots.length
    if (n === 0) {
      return this.current(ts)
    }

    // Fast path: all snapshots are older than requested time
    const last = this.snapshots.at(-1)
    if (last && last.ts < ts) {
      return this.current(ts)
    }

    // Fast path: check last two snapshots for recent queries
    for (let i = 1; i <= 2 && i <= n; i++) {
      const cur = this.snapshots.at(-i)
      const prev = this.snapshots.at(-(i + 1))

      if (!prev || prev.ts < ts) {
        if (cur && cur.ts >= ts) {
          return cur
        }
      }
    }

    // Fallback: binary search for first snapshot >= ts
    let l = 0
    let r = n - 1
    let res: Snapshot<keyof T, T[keyof T]> | null = null

    while (l <= r) {
      const m = (l + r) >> 1
      const cur = this.snapshots.at(m)

      if (cur && cur.ts >= ts) {
        res = cur
        r = m - 1 // try to find an earlier snapshot
      } else {
        l = m + 1 // search in later snapshots
      }
    }

    return res ?? this.current(ts)
  }

  /**
   * Return latest snapshot that satisfies the given predicate
   *
   * @returns A snapshot object with the previous Map state
   */
  latest(
    predicate: (
      values: Map<keyof T, T[keyof T]>,
      ts: number,
      stop: symbol
    ) => boolean | symbol
  ): Snapshot<keyof T, T[keyof T]> | undefined {
    for (let i = this.snapshots.length; i--; ) {
      const snapshot = this.snapshots.at(i)
      if (snapshot) {
        const stop = Symbol()
        const result = predicate(snapshot.map, snapshot.ts, stop)
        if (result === stop) return
        if (result) return snapshot
      }
    }
  }

  /**
   * Sets a value for the key with type safety, or removes the key if value is undefined.
   * Each key only accepts its corresponding value type as defined in T.
   * This allows using undefined as a sentinel value for deletion while chaining.
   *
   * @typeParam K - The specific key being set (inferred from the key parameter)
   * @param key - The key to set or remove
   * @param value - The value to set (type-checked against T[K]), or undefined to remove the key
   * @returns The SnapshotMap instance (for method chaining)
   *
   * @example
   * type Config = { count: number; status: 'active' | 'inactive' };
   * const map = new SnapshotMap<Config>(10);
   *
   * map.set('count', 42)         // ✅ OK
   *    .set('status', 'active')  // ✅ OK
   *    .set('count', undefined)  // ✅ OK - removes 'count'
   *    .set('count', 'wrong');   // ❌ Error: string not assignable to number
   */
  override set<K extends keyof T>(key: K, value: T[K] | undefined): this {
    if (value === undefined) {
      super.delete(key)
    } else {
      super.set(key, value)
    }
    return this
  }

  /**
   * Retrieves the value for the given key with type safety.
   * The return type is automatically inferred based on the key.
   *
   * @typeParam K - The specific key being retrieved (inferred from the key parameter)
   * @param key - The key to retrieve
   * @returns The value associated with the key, or undefined if not present
   *
   * @example
   * type Config = { count: number; status: 'active' | 'inactive' };
   * const map = new SnapshotMap<Config>(10);
   *
   * const count = map.get('count');    // Type: number | undefined
   * const status = map.get('status');  // Type: 'active' | 'inactive' | undefined
   */
  override get<K extends keyof T>(key: K): T[K] | undefined {
    return super.get(key) as T[K] | undefined
  }

  /**
   * Deletes a key and returns the map for chaining.
   * Unlike delete(), this doesn't return whether the key existed.
   *
   * @param key - The key to delete
   * @returns The SnapshotMap instance (for method chaining)
   *
   * @example
   * map.remove('count').remove('status');
   */
  remove(key: keyof T): this {
    super.delete(key)
    return this
  }

  /**
   * Stages a value update in a draft without modifying the actual Map.
   * The change will only be applied when commit() is called.
   * If value is undefined, the key will be removed on commit.
   * Uses strict equality (===) for comparison - only stages if value differs.
   *
   * @typeParam K - The specific key being updated (inferred from the key parameter)
   * @param key - The key to update
   * @param value - The new value (type-checked against T[K]), or undefined to remove the key
   * @returns The SnapshotMap instance (for method chaining)
   *
   * @example
   * type Config = { status: 'active' | 'inactive'; count: number };
   * const map = new SnapshotMap<Config>(10);
   *
   * map.update('status', 'active')   // Staged if different
   *    .update('count', 5)           // Staged if different
   *    .commit();                    // Snapshot created, then changes applied
   */
  update<K extends keyof T>(key: K, value: T[K] | undefined): this {
    // `undefined` allows to skip existing value update
    if (value === undefined) {
      return this
    }

    // Initialize draft on first update
    if (!this.draft) {
      this.draft = new Map()
    }

    // Only stage if this is actually a change
    const current = this.draft.has(key) ? this.draft.get(key) : this.get(key)
    if (current !== value) {
      this.draft.set(key, value)
    }

    return this
  }

  /**
   * Applies all staged changes from update() calls.
   * If any changes were staged, creates a snapshot BEFORE applying them.
   * Clears the draft after committing.
   *
   * @returns The SnapshotMap instance (for method chaining)
   *
   * @example
   * // Creates snapshot with old values, then applies new values
   * map.update('subscriptionId', subscription?.id.toString(10))
   *    .update('subscriptionName', subscription?.name ?? undefined)
   *    .commit();
   *
   * @example
   * // Duplicate event - no changes staged, no snapshot created
   * map.update('status', 'active')  // Already 'active'
   *    .update('count', 5)           // Already 5
   *    .commit();                    // No-op, draft is empty
   */
  commit(): this {
    if (!this.draft || this.draft.size === 0) {
      this.draft = null
      return this
    }

    // Snapshot BEFORE applying changes
    this.snapshot()

    // Apply all staged changes
    for (const [key, value] of this.draft) {
      this.set(key, value) // batch mode to suppress intermediate events
    }

    // Clear draft
    this.draft = null

    return this
  }

  /**
   * Discards all staged changes without applying them or creating a snapshot.
   *
   * @returns The SnapshotMap instance (for method chaining)
   *
   * @example
   * map.update('a', 1)
   *    .update('b', 2)
   *    .discard(); // Changes discarded, map unchanged
   */
  discard(): this {
    this.draft = null
    return this
  }

  /**
   * Allows tapping into the method chain.
   *
   * @param callback - A function that receives the SnapshotMap instance for inspection, modification, or side effects.
   * @returns The SnapshotMap instance (for method chaining)
   */
  tap(callback: (me: this) => void): this {
    callback(this)
    return this
  }

  /**
   * Notifies listeners of an update without modifying the Map or creating a snapshot.
   */
  notify(): this {
    this.updates.emit()
    return this
  }
}
